import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Plus, Search, Filter, FileText, Video, Image as ImageIcon, MoreHorizontal, Download, Edit, Trash2, Eye, UploadCloud, X, History, CheckCircle, Archive } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Badge } from '../../components/ui/Badge';
// Mock Data Types
type ContentType = 'toolkit' | 'video' | 'infografis' | 'dokumen';
type ContentStatus = 'published' | 'draft' | 'review' | 'archived';
interface ContentItem {
  id: string;
  title: string;
  type: ContentType;
  category: string;
  program: string;
  series?: string;
  version: string;
  date: string;
  validStart?: string;
  validEnd?: string;
  author: string;
  downloads: number;
  status: ContentStatus;
}
const mockContent: ContentItem[] = [{
  id: '1',
  title: 'Toolkit HPSN 2025 Lengkap',
  type: 'toolkit',
  category: 'Campaign',
  program: 'HPSN',
  version: 'v1.0',
  date: '2025-02-10',
  validStart: '2025-02-01',
  validEnd: '2025-03-01',
  author: 'Admin Pusat',
  downloads: 1250,
  status: 'published'
}, {
  id: '2',
  title: 'Video Tutorial Pemilahan Sampah Organik',
  type: 'video',
  category: 'Edukasi',
  program: 'Pilah Sampah',
  version: 'v1.2',
  date: '2025-02-15',
  author: 'Tim Kreatif',
  downloads: 850,
  status: 'published'
}, {
  id: '3',
  title: 'Infografis Alur Bank Sampah',
  type: 'infografis',
  category: 'Edukasi',
  program: 'Ekonomi Sirkular',
  version: 'v0.5',
  date: '2025-02-18',
  author: 'Admin Pusat',
  downloads: 0,
  status: 'draft'
}, {
  id: '4',
  title: 'Panduan Teknis Pelaporan Officer',
  type: 'dokumen',
  category: 'Internal',
  program: 'Internal',
  version: 'v2.0',
  date: '2025-01-05',
  author: 'Tim Teknis',
  downloads: 514,
  status: 'published'
}, {
  id: '5',
  title: 'Materi Sosialisasi RT/RW',
  type: 'toolkit',
  category: 'Sosialisasi',
  program: 'Pilah Sampah',
  version: 'v0.9',
  date: '2025-02-20',
  author: 'Admin Pusat',
  downloads: 0,
  status: 'review'
}, {
  id: '6',
  title: 'Toolkit HPSN 2024 (Old)',
  type: 'toolkit',
  category: 'Campaign',
  program: 'HPSN',
  version: 'v1.0',
  date: '2024-02-10',
  author: 'Admin Pusat',
  downloads: 2100,
  status: 'archived'
}];
export function ManajemenKontenPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null);
  const getIconByType = (type: ContentType) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4 text-red-500" data-id="element-1781" />;
      case 'infografis':
        return <ImageIcon className="h-4 w-4 text-blue-500" data-id="element-1782" />;
      case 'toolkit':
        return <FileText className="h-4 w-4 text-orange-500" data-id="element-1783" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" data-id="element-1784" />;
    }
  };
  const getStatusBadge = (status: ContentStatus) => {
    switch (status) {
      case 'published':
        return <StatusBadge status="hijau" data-id="element-1785">Terbit</StatusBadge>;
      case 'draft':
        return <StatusBadge status="abu" data-id="element-1786">Draft</StatusBadge>;
      case 'review':
        return <StatusBadge status="kuning" data-id="element-1787">Review</StatusBadge>;
      case 'archived':
        return <StatusBadge status="merah" data-id="element-1788">Arsip</StatusBadge>;
    }
  };
  const columns = [{
    header: 'Judul Konten',
    cell: (item: ContentItem) => <div className="flex items-center gap-3" data-id="element-1789">
          <div className="p-2 bg-gray-100 rounded-lg" data-id="element-1790">
            {getIconByType(item.type)}
          </div>
          <div data-id="element-1791">
            <div className="flex items-center gap-2" data-id="element-1792">
              <p className="font-medium text-gray-900" data-id="element-1793">{item.title}</p>
              <Badge variant="secondary" className="text-[10px] h-5 px-1.5" data-id="element-1794">
                {item.version}
              </Badge>
            </div>
            <p className="text-xs text-gray-500 capitalize" data-id="element-1795">
              {item.program} • {item.category}
            </p>
          </div>
        </div>
  }, {
    header: 'Tanggal',
    accessorKey: 'date' as keyof ContentItem,
    cell: (item: ContentItem) => <span className="text-gray-600 text-sm" data-id="element-1796">{item.date}</span>
  }, {
    header: 'Penulis',
    accessorKey: 'author' as keyof ContentItem,
    cell: (item: ContentItem) => <span className="text-gray-600 text-sm" data-id="element-1797">{item.author}</span>
  }, {
    header: 'Unduhan',
    accessorKey: 'downloads' as keyof ContentItem,
    cell: (item: ContentItem) => <span className="text-gray-600 font-medium text-sm" data-id="element-1798">
          {item.downloads.toLocaleString()}
        </span>
  }, {
    header: 'Status',
    cell: (item: ContentItem) => getStatusBadge(item.status)
  }, {
    header: 'Aksi',
    cell: (item: ContentItem) => <div className="flex items-center gap-2" data-id="element-1799">
          <button className="p-1 text-gray-400 hover:text-blue-600 transition-colors" title="Lihat" onClick={() => setSelectedContent(item)} data-id="element-1800">
            <Eye className="h-4 w-4" data-id="element-1801" />
          </button>
          <button className="p-1 text-gray-400 hover:text-green-600 transition-colors" title="Edit" onClick={() => {
        setSelectedContent(item);
        setIsCreateModalOpen(true);
      }} data-id="element-1802">
            <Edit className="h-4 w-4" data-id="element-1803" />
          </button>
        </div>
  }];
  return <CommandCenterLayout title="Bank Konten & Toolkit" subtitle="Manajemen narasi tunggal dan distribusi materi edukasi nasional" data-id="element-1804">
      <div className="space-y-6" data-id="element-1805">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1806">
          <Card data-id="element-1807">
            <CardContent className="p-6 flex items-center justify-between" data-id="element-1808">
              <div data-id="element-1809">
                <p className="text-sm font-medium text-gray-500" data-id="element-1810">
                  Total Konten
                </p>
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1811">1,248</h3>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-1812">
                  +12 bulan ini
                </p>
              </div>
              <div className="p-3 bg-blue-50 text-blue-600 rounded-full" data-id="element-1813">
                <FileText className="h-6 w-6" data-id="element-1814" />
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1815">
            <CardContent className="p-6 flex items-center justify-between" data-id="element-1816">
              <div data-id="element-1817">
                <p className="text-sm font-medium text-gray-500" data-id="element-1818">
                  Total Unduhan
                </p>
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-1819">85.4K</h3>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-1820">
                  +5.2% vs bulan lalu
                </p>
              </div>
              <div className="p-3 bg-green-50 text-green-600 rounded-full" data-id="element-1821">
                <Download className="h-6 w-6" data-id="element-1822" />
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-1823">
            <CardContent className="p-6 flex items-center justify-between" data-id="element-1824">
              <div data-id="element-1825">
                <p className="text-sm font-medium text-gray-500" data-id="element-1826">
                  Konten Populer
                </p>
                <h3 className="text-lg font-bold text-gray-900 truncate max-w-[150px]" data-id="element-1827">
                  Toolkit HPSN 2025
                </h3>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1828">1,250 unduhan</p>
              </div>
              <div className="p-3 bg-orange-50 text-orange-600 rounded-full" data-id="element-1829">
                <History className="h-6 w-6" data-id="element-1830" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area */}
        <Card data-id="element-1831">
          <CardHeader className="flex flex-row items-center justify-between pb-4" data-id="element-1832">
            <div data-id="element-1833">
              <CardTitle data-id="element-1834">Daftar Konten</CardTitle>
              <CardDescription data-id="element-1835">
                Kelola semua materi edukasi, toolkit, dan dokumen
              </CardDescription>
            </div>
            <Button className="bg-primary-600 hover:bg-primary-700 text-white gap-2" onClick={() => {
            setSelectedContent(null);
            setIsCreateModalOpen(true);
          }} data-id="element-1836">
              <Plus className="h-4 w-4" data-id="element-1837" />
              Tambah Konten
            </Button>
          </CardHeader>
          <CardContent data-id="element-1838">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6" data-id="element-1839">
              <div className="relative flex-1" data-id="element-1840">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-1841" />
                <Input placeholder="Cari judul konten..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1842" />
              </div>
              <div className="flex gap-2" data-id="element-1843">
                <Select data-id="element-1844">
                  <option data-id="element-1845">Semua Program</option>
                  <option data-id="element-1846">HPSN</option>
                  <option data-id="element-1847">Pilah Sampah</option>
                  <option data-id="element-1848">Ekonomi Sirkular</option>
                </Select>
                <Select data-id="element-1849">
                  <option data-id="element-1850">Semua Status</option>
                  <option data-id="element-1851">Terbit</option>
                  <option data-id="element-1852">Draft</option>
                  <option data-id="element-1853">Review</option>
                  <option data-id="element-1854">Arsip</option>
                </Select>
                <Button variant="outline" className="gap-2" data-id="element-1855">
                  <Filter className="h-4 w-4" data-id="element-1856" />
                  Filter
                </Button>
              </div>
            </div>

            {/* Table */}
            <DataTable data={mockContent} columns={columns} keyExtractor={item => item.id} data-id="element-1857" />
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Content Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title={selectedContent ? 'Edit Konten' : 'Tambah Konten Baru'} description="Isi detail konten untuk dipublikasikan ke portal" size="lg" footer={<div className="flex justify-end gap-2 w-full" data-id="element-1859">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)} data-id="element-1860">
              Batal
            </Button>
            <Button variant="outline" data-id="element-1861">Simpan Draft</Button>
            <Button className="bg-primary-600 text-white" data-id="element-1862">
              {selectedContent ? 'Update & Publish' : 'Publish Konten'}
            </Button>
          </div>} data-id="element-1858">
        <div className="space-y-4" data-id="element-1863">
          <div className="grid grid-cols-2 gap-4" data-id="element-1864">
            <div className="col-span-2" data-id="element-1865">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1866">
                Judul Konten
              </label>
              <Input placeholder="Masukkan judul konten..." defaultValue={selectedContent?.title} data-id="element-1867" />
            </div>

            <div data-id="element-1868">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1869">
                Program (Wajib)
              </label>
              <Select defaultValue={selectedContent?.program || ''} data-id="element-1870">
                <option value="" disabled data-id="element-1871">
                  Pilih Program
                </option>
                <option value="HPSN" data-id="element-1872">HPSN</option>
                <option value="Pilah Sampah" data-id="element-1873">Pilah Sampah</option>
                <option value="Ekonomi Sirkular" data-id="element-1874">Ekonomi Sirkular</option>
                <option value="Internal" data-id="element-1875">Internal Officer</option>
              </Select>
            </div>

            <div data-id="element-1876">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1877">
                Series (Opsional)
              </label>
              <Select defaultValue={selectedContent?.series || ''} data-id="element-1878">
                <option value="" data-id="element-1879">Tidak ada series</option>
                <option value="Ramadhan" data-id="element-1880">Ramadhan Minim Sampah</option>
                <option value="Idul Fitri" data-id="element-1881">Mudik Minim Sampah</option>
              </Select>
            </div>

            <div data-id="element-1882">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1883">
                Tipe Konten
              </label>
              <Select defaultValue={selectedContent?.type || 'toolkit'} data-id="element-1884">
                <option value="toolkit" data-id="element-1885">Toolkit Campaign</option>
                <option value="video" data-id="element-1886">Video Edukasi</option>
                <option value="infografis" data-id="element-1887">Infografis</option>
                <option value="dokumen" data-id="element-1888">Dokumen Teknis</option>
              </Select>
            </div>

            <div data-id="element-1889">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1890">
                Versi
              </label>
              <Input placeholder="v1.0" defaultValue={selectedContent?.version || 'v1.0'} data-id="element-1891" />
            </div>

            <div data-id="element-1892">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1893">
                Berlaku Mulai
              </label>
              <Input type="date" defaultValue={selectedContent?.validStart} data-id="element-1894" />
            </div>

            <div data-id="element-1895">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1896">
                Berlaku Sampai
              </label>
              <Input type="date" defaultValue={selectedContent?.validEnd} data-id="element-1897" />
            </div>

            <div className="col-span-2" data-id="element-1898">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1899">
                Deskripsi Singkat
              </label>
              <Textarea placeholder="Jelaskan isi konten ini..." className="h-20" data-id="element-1900" />
            </div>

            <div className="col-span-2" data-id="element-1901">
              <label className="text-sm font-medium text-gray-700 mb-1 block" data-id="element-1902">
                Upload File
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-1903">
                <UploadCloud className="h-10 w-10 text-gray-400 mx-auto mb-2" data-id="element-1904" />
                <p className="text-sm text-gray-600 font-medium" data-id="element-1905">
                  Klik untuk upload atau drag & drop
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-1906">
                  PDF, MP4, JPG, PNG (Max. 50MB)
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={!!selectedContent && !isCreateModalOpen} onClose={() => setSelectedContent(null)} title="Preview Konten" size="lg" data-id="element-1907">
        {selectedContent && <div className="space-y-4" data-id="element-1908">
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border" data-id="element-1909">
              {selectedContent.type === 'video' ? <Video className="h-16 w-16 text-gray-400" data-id="element-1910" /> : <FileText className="h-16 w-16 text-gray-400" data-id="element-1911" />}
            </div>
            <div data-id="element-1912">
              <div className="flex items-center gap-2" data-id="element-1913">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1914">
                  {selectedContent.title}
                </h3>
                <Badge data-id="element-1915">{selectedContent.version}</Badge>
              </div>
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500" data-id="element-1916">
                <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-medium uppercase" data-id="element-1917">
                  {selectedContent.type}
                </span>
                <span data-id="element-1918">•</span>
                <span data-id="element-1919">{selectedContent.date}</span>
                <span data-id="element-1920">•</span>
                <span data-id="element-1921">Oleh {selectedContent.author}</span>
              </div>
              <p className="mt-4 text-gray-600" data-id="element-1922">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </p>
            </div>
            <div className="flex gap-2 pt-4 border-t" data-id="element-1923">
              <Button className="w-full bg-primary-600 text-white gap-2" data-id="element-1924">
                <Download className="h-4 w-4" data-id="element-1925" /> Download Materi
              </Button>
            </div>
          </div>}
      </Modal>
    </CommandCenterLayout>;
}